import { describeHttpError, isHttpErrorLike, sanitizeSentryBreadcrumb, sanitizeSentryEvent } from "@relevoo/shared";
import * as Sentry from "@sentry/react";
import { env } from "@/src/common/constants/env";

export const ErrorBoundary = Sentry.ErrorBoundary;

let initialized = false;

export const initSentry = (): void => {
	if (!env.SENTRY_DSN || initialized) {
		return;
	}
	initialized = true;
	Sentry.init({
		dsn: env.SENTRY_DSN,
		enabled: !env.DEV,
		dataCollection: {
			userInfo: false,
			cookies: false,
			httpHeaders: false,
			httpBodies: [],
			urlQueryParams: false,
			graphQL: { document: false, variables: false },
			genAI: { inputs: false, outputs: false },
			databaseQueryData: false,
			queues: false,
			stackFrameVariables: false,
		},
		ignoreErrors: ["InvalidStateError: Transition was aborted", "AbortError", "ResizeObserver loop"],
		beforeSend: (event) => {
			sanitizeSentryEvent(event);
			return event;
		},
		beforeBreadcrumb: (breadcrumb) => {
			sanitizeSentryBreadcrumb(breadcrumb);
			return breadcrumb;
		},
	});
};

export type ReportError = (error: unknown, context?: Record<string, unknown>) => void;

const sentryReportError: ReportError = (error, context) => {
	if (isHttpErrorLike(error)) {
		error.message = describeHttpError(error);
		Sentry.captureException(error, { extra: { ...context, status: error.status, value: error.value } });
		return;
	}
	Sentry.captureException(error, context ? { extra: context } : undefined);
};

let impl: ReportError = sentryReportError;

export const reportError: ReportError = (error, context) => {
	impl(error, context);
};

export const __setReportErrorImpl = (next: ReportError): void => {
	impl = next;
};

export const __resetReportErrorImpl = (): void => {
	impl = sentryReportError;
};
